import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Star, CheckCircle, MapPin, Clock, Truck, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';

const HomePage = () => {
  const [selectedSample, setSelectedSample] = useState(null);

  const sameDaySamples = [
    {
      name: 'Business Cards',
      image: '/images/business-cards.png',
      desc: 'Premium business cards on heavy stock — matte, gloss, or soft-touch finishes available.',
    },
    {
      name: 'Flyers',
      image: '/images/flyers-brochures.png',
      desc: 'Vibrant full-color flyers on 100lb gloss or matte stock — perfect for events and promotions.',
    },
    {
      name: 'Postcards',
      image: '/images/postcards.png',
      desc: 'Eye-catching postcards in standard or custom sizes — great for direct mail campaigns.',
    },
    {
      name: 'Letterheads',
      image: '/images/letterhead.png',
      desc: 'Professional letterhead printed on premium 24lb or 32lb bond paper.',
    },
    {
      name: 'Envelopes',
      image: '/images/envelopes.png',
      desc: 'Custom-printed envelopes — #10, A7, or custom sizes with full-color branding.',
    },
  ];
  const services = [
    { id: 'business-cards', name: 'Business Cards', desc: 'Premium quality cards that make a statement', image: '/images/business-cards.png' },
    { id: 'flyers-brochures', name: 'Flyers & Brochures', desc: 'Eye-catching marketing materials', image: '/images/flyers-brochures.png' },
    { id: 'posters-banners', name: 'Posters & Banners', desc: 'Large format printing that stands out', image: '/images/posters-banners.png' },
    { id: 'digital-printing', name: 'Digital Printing', desc: 'Fast turnaround, perfect results', image: '/images/xerox-versant-280.webp' },
  ];

  const reviews = [
    {
      name: 'Michael Torres',
      role: 'Marketing Director',
      text: 'Charles Print delivered our business cards in record time. The quality exceeded our expectations. Highly recommend!',
      rating: 5,
    },
    {
      name: 'Sarah Chen',
      role: 'Small Business Owner',
      text: 'Best print shop in Manhattan! Professional service, competitive prices, and always on time.',
      rating: 5,
    },
    {
      name: 'David Miller',
      role: 'Event Coordinator',
      text: 'We use Charles Print for all our event materials. They never disappoint. True NYC quality.',
      rating: 5,
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section with NYC Background */}
      <section 
        className="relative flex items-center min-h-[calc(100vh-170px)] py-8 md:py-10 overflow-hidden" 
        data-testid="hero-section"
      >
        {/* Background video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="https://images.unsplash.com/photo-1534430480872-3498386e7856?auto=format&fit=crop&w=1920&q=80"
          className="absolute inset-0 w-full h-full object-cover z-0"
          aria-hidden="true"
        >
          <source src="/videos/nyc-skyline.mp4" type="video/mp4" />
        </video>

        {/* White gradient overlay for text legibility */}
        <div
          className="absolute inset-0 z-0"
          style={{
            background: 'linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.4) 100%)',
          }}
          aria-hidden="true"
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="max-w-3xl">
            {/* Trust badge */}
            <div className="inline-flex items-center gap-2 bg-zinc-100 px-4 py-2 rounded-sm mb-5 animate-fade-in-up">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={`hero-star-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-sm font-medium text-zinc-700">5-Star Rated on Google</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95] text-[#3EACDB] animate-fade-in-up animation-delay-100">
              Same Day<br />
              Printing Services<br />
              in NYC
            </h1>

            <p className="mt-5 text-base md:text-lg text-zinc-600 leading-relaxed max-w-xl animate-fade-in-up animation-delay-200">
              From business cards to large format banners, we deliver exceptional print quality with fast turnaround times. Trusted by NYC businesses since day one.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 animate-fade-in-up animation-delay-300">
              <Link to="/quote" data-testid="hero-quote-btn">
                <Button className="h-12 px-7 text-sm font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm w-full sm:w-auto">
                  Make a Rush Order
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="tel:+12129711334" data-testid="hero-call-btn">
                <Button variant="outline" className="h-12 px-7 text-sm font-bold uppercase tracking-wider rounded-sm w-full sm:w-auto border-2">
                  <Phone className="mr-2 w-4 h-4" />
                  (212) 971-1334
                </Button>
              </a>
            </div>

            {/* Quick info */}
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-500 animate-fade-in-up animation-delay-400">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#3EACDB]" />
                <span>247 W 35th St, Suite 203, NYC</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#3EACDB]" />
                <span>Mon-Fri: 9AM - 5:30PM · Sat-Sun: By Appointment</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 md:py-32 bg-zinc-50" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16">
            <div>
              <span className="text-sm font-medium tracking-widest uppercase text-[#3EACDB]">What We Do</span>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mt-4">
                Our Services
              </h2>
            </div>
            <Link 
              to="/services" 
              className="mt-6 lg:mt-0 inline-flex items-center text-[#3EACDB] font-semibold hover:underline"
              data-testid="view-all-services"
            >
              View All Services
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Link
                key={service.id}
                to={`/services#${service.id}`}
                className="service-card group bg-white border border-zinc-200 hover:border-[#3EACDB]/50 hover:shadow-lg transition-all cursor-pointer flex flex-col overflow-hidden"
                data-testid={`service-card-${index}`}
              >
                <div className="relative w-full aspect-[4/3] overflow-hidden bg-zinc-100">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="w-10 h-10 bg-[#3EACDB]/10 group-hover:bg-[#3EACDB] rounded-sm flex items-center justify-center mb-4 transition-colors">
                    <CheckCircle className="w-5 h-5 text-[#3EACDB] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2 group-hover:text-[#3EACDB] transition-colors">{service.name}</h3>
                  <p className="text-zinc-600 text-sm leading-relaxed">{service.desc}</p>
                  <span className="mt-4 inline-flex items-center text-[#3EACDB] text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more
                    <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-white" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-medium tracking-widest uppercase text-[#3EACDB]">Why Charles Print</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mt-4">
                NYC Quality You Can Trust
              </h2>
              <p className="mt-6 text-lg text-zinc-600 leading-relaxed">
                Located in the heart of Manhattan, Charles Print has been serving businesses and professionals with premium printing services. We combine state-of-the-art technology with old-fashioned attention to detail.
              </p>
              
              <ul className="mt-8 space-y-4">
                {[
                  'Fast turnaround times - same day available',
                  'Premium paper stocks and finishes',
                  'Competitive pricing for all budgets',
                  'Expert design assistance available',
                  'Convenient Midtown Manhattan location',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#3EACDB] mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-700">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <Link to="/about" data-testid="learn-more-btn">
                  <Button variant="outline" className="h-12 px-6 text-sm font-semibold uppercase tracking-wider rounded-sm border-2">
                    Learn More About Us
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1614036634955-ae5e90f9b9eb?auto=format&fit=crop&w=800&q=85"
                alt="Charles Print shop work — branding & printed materials"
                className="w-full rounded-sm shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#3EACDB] text-white p-6 rounded-sm hidden md:block">
                <p className="font-heading text-3xl font-bold">15+</p>
                <p className="text-sm uppercase tracking-wider mt-1">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 md:py-32 bg-zinc-900 text-white" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={`reviews-star-${i}`} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
              What Our Clients Say
            </h2>
            <p className="mt-4 text-zinc-400 max-w-2xl mx-auto">
              Don't just take our word for it. See what NYC businesses say about our printing services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div
                key={review.name}
                className="review-card bg-zinc-800 border border-zinc-700 p-8 rounded-sm"
                data-testid={`review-card-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={`${review.name}-star-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-zinc-300 leading-relaxed mb-6">"{review.text}"</p>
                <div>
                  <p className="font-semibold">{review.name}</p>
                  <p className="text-sm text-zinc-500">{review.role}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/reviews" data-testid="view-all-reviews">
              <Button variant="outline" className="h-12 px-8 text-sm font-semibold uppercase tracking-wider rounded-sm border-2 border-white text-white hover:bg-white hover:text-zinc-900">
                View All Reviews
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#3EACDB] text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
            Ready to Start Your Project?
          </h2>
          <p className="mt-6 text-xl text-blue-100 max-w-2xl mx-auto">
            Get a free quote today or visit our Midtown Manhattan location.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/quote" data-testid="cta-quote-btn">
              <Button className="h-14 px-8 text-base font-bold uppercase tracking-wider bg-white text-[#3EACDB] hover:bg-zinc-100 rounded-sm w-full sm:w-auto">
                Make a Rush Order
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+12129711334" data-testid="cta-call-btn">
              <Button variant="outline" className="h-14 px-8 text-base font-bold uppercase tracking-wider rounded-sm border-2 border-white text-white hover:bg-white hover:text-[#3EACDB] w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* We Deliver Section */}
      <section 
        className="py-20 md:py-28 relative" 
        data-testid="we-deliver-section"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(9,9,11,0.85), rgba(9,9,11,0.9)), url('https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=1920&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <Truck className="w-12 h-12 md:w-16 md:h-16 text-[#3EACDB]" />
          </div>
          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-white">
            <span className="text-[#E33135]">We</span> <span className="text-[#3EACDB]">Deliver</span>
          </h2>
          <p className="mt-6 text-xl text-zinc-300 max-w-2xl mx-auto">
            Fast and reliable delivery throughout New York City.
          </p>
          
          {/* Same Day Delivery Badge */}
          <div className="mt-8 inline-flex items-center gap-3 bg-[#3EACDB] text-white px-6 py-3 rounded-sm">
            <Clock className="w-6 h-6" />
            <span className="text-lg font-bold uppercase tracking-wider">Same Day Delivery Available on Select Jobs</span>
          </div>
          
          <div className="mt-10 flex flex-wrap justify-center gap-8 text-white">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-[#3EACDB]" />
              <span className="text-lg">Manhattan</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-[#3EACDB]" />
              <span className="text-lg">Brooklyn</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-[#3EACDB]" />
              <span className="text-lg">Queens</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-[#3EACDB]" />
              <span className="text-lg">Bronx</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-[#3EACDB]" />
              <span className="text-lg">Staten Island</span>
            </div>
          </div>
          
          {/* Same Day Jobs List */}
          <div className="mt-10 bg-white/10 backdrop-blur-sm rounded-sm p-6 max-w-2xl mx-auto">
            <p className="text-white font-semibold mb-2">Same Day Delivery Available For:</p>
            <p className="text-zinc-300 text-xs mb-4">Click any item below to see a sample</p>
            <div className="flex flex-wrap justify-center gap-4 text-zinc-300">
              {sameDaySamples.map((sample) => (
                <button
                  key={sample.name}
                  type="button"
                  onClick={() => setSelectedSample(sample)}
                  className="bg-white/10 hover:bg-[#3EACDB] hover:text-white px-4 py-2 rounded-sm transition-colors cursor-pointer text-sm font-medium"
                  data-testid={`sample-${sample.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {sample.name}
                </button>
              ))}
            </div>
            <p className="text-zinc-400 text-sm mt-4">*Subject to availability. Call for rush orders.</p>
          </div>
          
          <div className="mt-10">
            <Link to="/contact" data-testid="delivery-contact-btn">
              <Button className="h-14 px-8 text-base font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm">
                Contact Us for Delivery Info
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sample Image Dialog */}
      <Dialog open={!!selectedSample} onOpenChange={(open) => !open && setSelectedSample(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-white" data-testid="sample-dialog">
          {selectedSample && (
            <>
              <DialogTitle className="sr-only">{selectedSample.name} Sample</DialogTitle>
              <DialogDescription className="sr-only">{selectedSample.desc}</DialogDescription>
              <div className="relative">
                <img
                  src={selectedSample.image}
                  alt={`${selectedSample.name} sample`}
                  className="w-full h-[400px] md:h-[500px] object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1586281010691-3a8ad4bb47f9?auto=format&fit=crop&w=1200&q=80';
                  }}
                />
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/60 to-transparent p-6">
                  <h3 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight text-white">
                    {selectedSample.name}
                  </h3>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <p className="text-zinc-700 text-base md:text-lg leading-relaxed">
                  {selectedSample.desc}
                </p>
                <div className="mt-6 flex items-center gap-2 text-[#3EACDB] font-semibold">
                  <Clock className="w-5 h-5" />
                  <span>Same-day delivery available — call to confirm rush capacity.</span>
                </div>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Link to="/quote" onClick={() => setSelectedSample(null)} className="flex-1">
                    <Button className="w-full h-12 text-sm font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm" data-testid="sample-dialog-order-btn">
                      Make a Rush Order
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:+12129711334" className="flex-1">
                    <Button variant="outline" className="w-full h-12 text-sm font-bold uppercase tracking-wider rounded-sm border-2">
                      <Phone className="mr-2 w-4 h-4" />
                      Call (212) 971-1334
                    </Button>
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
